<template>
    <div v-if="!loading" class="loading-wrapper">
        <div class="share-wrapper" v-show="!showNewTabOpenText">
            <tabs v-model:value="tabInView" class="tabs">
                <tab-list>
                    <tab value="shareReport">Share report</tab>
                    <tab value="shareSlide">Share slide</tab>
                    <tab value="generateTemporaryLink" v-if="canSeeGenerateTempLink">Generate temporary link (LCP internal)</tab>
                </tab-list>
            </tabs>
            <div v-show="tabInView === 'shareReport'" class="share-report">
                <share-document-url :title="props.pdfDetails.documentTitle" @external-share-started="showNewTabOpenText = true" />
            </div>
            <div v-show="tabInView === 'shareSlide'" class="share-slide">
                <pdf-page-selector
                    v-if="tabInView === 'shareSlide'"
                    ref="pageSelectorRef"
                    :pdf="props.pdf"
                    :initial-page-index="initialSlidePageIndex"
                    preview-height="calc(50vh - 16rem)"
                    class="share-slide-selector"
                />
                <div class="share-options">
                    <div class="button-holder">
                        <a :href="linkedInUrl" @click.prevent="shareLinkedin" v-if="userAccessStore.userAccess.isLcp">
                            <primevue-button class="plain-btn">
                                LinkedIn
                                <font-awesome-icon :icon="['fab', 'linkedin']" />
                            </primevue-button>
                        </a>
                        <primevue-button @click="shareEmail" class="plain-btn" v-if="userAccessStore.userAccess.isLcp">
                            Email
                            <font-awesome-icon :icon="['fas', 'envelope']" />
                        </primevue-button>
                        <primevue-button @click="copyImageToClipboard" class="plain-btn">
                            Copy to clipboard
                            <font-awesome-icon :icon="['fas', 'copy']" />
                        </primevue-button>
                    </div>
                </div>
            </div>
            <div v-if="tabInView === 'generateTemporaryLink'" class="generate-temp-link">
                <share-document-via-link :canSeeGenerateTempLink="canSeeGenerateTempLink" :pdf-details="props.pdfDetails" :pdf="props.pdf" :tabInView="tabInView" />
            </div>
        </div>
        <div v-show="showNewTabOpenText" class="currently-sharing">
            <div>
                A new window has opened to enable you to share, and the {{ tabInView === 'shareReport' ? 'report link' : 'slide' }} has been copied to your clipboard.
            </div>
            <div>
                Please note, you will need to manually paste this into your post.
            </div>
            <primevue-button
                text
                class="plain-btn"
                @click="returnToShareScreen">
                Return to share
                <font-awesome-icon :icon="['fas', 'arrow-left']" />
            </primevue-button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { IPdfDetails } from '@/interfaces/deltaGpt/IDocument.ts';
import { publishToast } from '@/utils/Toast.ts';
import { useToast } from 'primevue/usetoast';
import {
    computed, onMounted, ref,
} from 'vue';
import primevueButton from 'primevue/button';
import { useUserAccessStore } from '@/stores/userAccessStore.ts';
import { useRoute } from 'vue-router';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import PdfPageSelector from './PdfPageSelector.vue';
import ShareDocumentViaLink from './ShareDocumentViaLink.vue';
import ShareDocumentUrl from './ShareDocumentUrl.vue';

const toast = useToast();

const props = defineProps<{ pdfDetails: IPdfDetails; pdf: Uint8Array; initialSlideNumber?: number | undefined }>();

const userAccessStore = useUserAccessStore();

const tabInView = ref<string>(props.initialSlideNumber ? 'shareSlide' : 'shareReport');
const pageSelectorRef = ref<InstanceType<typeof PdfPageSelector> | null>(null);
const showNewTabOpenText = ref<boolean>(false);

// Needed for an annoying issue with primevue tabs not displaying properly as the modal grows
const loading = ref<boolean>(true);

const urlOfPage = computed<string>(() => window.location.origin + useRoute().fullPath);
const linkedInUrl = computed<string>(() => `https://www.linkedin.com/sharing/share-offsite/?url=${urlOfPage.value}`);

const canSeeGenerateTempLink = computed<boolean>(() => userAccessStore.userAccess.research.canShareTemporaryReports || userAccessStore.userAccess.isLcp);
const initialSlidePageIndex = computed<number>(() => Math.max((props.initialSlideNumber ?? 1) - 1, 0));

async function copyImage (): Promise<boolean> {
    const copied = await pageSelectorRef.value?.copyCurrentPageImage();
    if (!copied) {
        publishToast(toast, 'Failed to copy image', 'error');
        return false;
    }
    return true;
}

function returnToShareScreen (): void {
    showNewTabOpenText.value = false;
}

async function copyImageToClipboard (): Promise<void> {
    const copied = await copyImage();
    if (!copied) return;
    publishToast(toast, 'Copied to clipboard.', 'success');
}

async function shareLinkedin (): Promise<void> {
    const copied = await copyImage();
    if (!copied) return;
    publishToast(toast, 'Copied to clipboard. Please paste it on the Linkedin window.', 'success');
    window.open(linkedInUrl.value, 'linkedin-popup', 'width=600,height=600');
    showNewTabOpenText.value = true;
}

async function shareEmail (): Promise<void> {
    const copied = await copyImage();
    if (!copied) return;
    const body = encodeURIComponent(`[PASTE IMAGE HERE]\n\nImage source: ${urlOfPage.value}`);
    window.location.href = `mailto:?body=${body}`;
    showNewTabOpenText.value = true;
}

onMounted(() => {
    setTimeout(() => {
        loading.value = false;
    }, 100);
});

</script>

<style lang="scss" scoped>
.loading-wrapper {
    height: 100%;
}

.share-wrapper {
    height: 100%;
    display: flex;
    flex-direction: column;
    .button-holder {
        display: flex;
        gap: var(--defaultSpacing);
    }
    .teams-image {
        width: 2rem;
        height: 2rem;
        border-radius: var(--borderRadiusInner);
    }
    .share-report {
        display: flex;
        justify-content: center;
        padding-top: 8rem;
        height: 100%;
        .share-report-content {
            display: flex;
            flex-direction: column;
            gap: var(--extraLargeSpacing);
            padding: var(--defaultSpacing);
            justify-content: space-between;
            border-radius: var(--borderRadiusOuter);

            @include responsive(sm) {
                width: 100%;
                justify-content: flex-start;
            }

            .link {
                cursor: pointer;
                color: var(--actionColour);
            }
            .copy-link-input {
                width: 100%;
                display: flex;
                justify-content: center;
                .p-inputgroup {
                    width: 60rem;
                }
            }
            .button-holder {
                justify-content: center;
            }
        }
    }
    .share-slide {
        display: flex;
        flex: 1;
        flex-direction: column;
        align-items: center;
        height: 100%;
        gap: var(--largeSpacing);
        padding: var(--defaultSpacing);
        .share-slide-selector {
            width: 100%;
            max-width: 100%;
        }
        .share-options {
            display: flex;
            justify-content: center;
        }
    }
    .generate-temp-link {
        margin: var(--defaultSpacing);
    }
}
.currently-sharing {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    flex-direction: column;
    gap: var(--extraLargeSpacing);
}
</style>
